import {
  faunaForm,
  type FaunaForm,
} from '@/fauna/FaunaGeometry';
import {
  poseHeightMeters,
  poseLengthMeters,
  type FaunaDefinition,
  type FaunaPose,
} from '@/fauna/FaunaDefinition';
import { safeColor } from './FloraThumbnail';

/**
 * Palette thumbnails for fauna, drawn as inline SVG silhouettes in the record's own two colours.
 *
 * Same bargain `FloraThumbnail` strikes and for the same reasons: an offscreen 3D preview would cost
 * a framebuffer and a render pass per catalog entry to fill a 48px tile, and a vector silhouette
 * keyed to the same form template communicates the thing a designer is actually reading — is that a
 * bird or a mammal, is it standing or crouched — at any DPI, instantly.
 *
 * ## Every tile is drawn to the same scale
 *
 * The one decision here worth defending. A palette that fits each creature to its tile makes a
 * bumblebee and a white-tailed deer the same size on screen, which is precisely backwards for a
 * catalog whose whole purpose is scale. So the drawing is proportional across the catalog on a
 * shared log scale, with a floor so a 22 mm bee is still visible: a deer fills its tile, a rabbit
 * takes a third of it, and an insect is a speck you can nevertheless identify from its form.
 */

const VIEW = { width: 64, height: 48 } as const;
const GROUND = 42;

/**
 * Screen height in the tile for a real height in metres.
 *
 * Logarithmic, over a range from 20 mm to 2 m — the actual span of the catalog. Linear would give a
 * bumblebee a third of a pixel; a fit-to-tile scale would tell the lie described above. A log scale
 * keeps the ordering honest and everything legible, which is the trade every field guide makes.
 */
function screenHeight(meters: number): number {
  const min = 0.02;
  const max = 2;
  const clamped = Math.min(max, Math.max(min, meters));
  const t = Math.log(clamped / min) / Math.log(max / min);
  return 4 + t * 30;
}

export function faunaThumbnailSvg(definition: FaunaDefinition, pose: FaunaPose): string {
  const body = safeColor(definition.rendering.colors.body, '#7d7566');
  const accent = safeColor(definition.rendering.colors.accent, '#d8d3cb');

  const realHeight = poseHeightMeters(definition, pose);
  const realLength = poseLengthMeters(definition, pose);
  const h = screenHeight(realHeight);
  // Length follows the record's own aspect ratio rather than being scaled separately, so a dachshund
  // silhouette stays long and a sitting rabbit stays compact.
  const w = Math.max(4, h * (realLength / Math.max(realHeight, 0.001)));

  const airborne = (pose.attachment ?? definition.attachment[0]) === 'air';
  const baseline = airborne ? GROUND - 12 : GROUND;
  const cx = VIEW.width / 2;

  const shape = drawForm(faunaForm(definition), { cx, baseline, w, h, body, accent, airborne });

  return [
    `<svg viewBox="0 0 ${VIEW.width} ${VIEW.height}" width="100%" height="100%" role="img" aria-hidden="true">`,
    `<line x1="4" y1="${GROUND}.5" x2="${VIEW.width - 4}" y2="${GROUND}.5" stroke="currentColor" stroke-opacity="0.22" stroke-width="1"/>`,
    shape,
    '</svg>',
  ].join('');
}

interface DrawArgs {
  cx: number;
  baseline: number;
  w: number;
  h: number;
  body: string;
  accent: string;
  airborne: boolean;
}

function drawForm(form: FaunaForm, a: DrawArgs): string {
  switch (form) {
    case 'quadruped':
      return quadruped(a);
    case 'bird':
      return bird(a);
    case 'insect':
      return insect(a);
    case 'squat':
      return squat(a);
    case 'serpentine':
      return serpentine(a);
    case 'figure':
      return figure(a);
  }
}

const round = (n: number): string => n.toFixed(2);

/**
 * A quadruped: haunch, barrel, neck, head, ears, and a tail that says which end is which.
 *
 * This one took three attempts, and what it cost is worth writing down because the bird template
 * got it right by accident. Two earlier versions — thin legs at the extremes, a wide flat ellipse,
 * and a pale circle floating above one end — read as a **table**, then as a mushroom. Nothing about
 * the proportions was wrong; the *assembly* was.
 *
 * What fixed it:
 *
 * - **The head is body-coloured, not accent-coloured.** A pale ball above a dark body is two
 *   objects however much they overlap. The accent moved to the tail and the eye, which is where a
 *   cottontail's white actually is, and a deer's.
 * - **A neck that is visible outside the barrel.** The previous version drew one entirely inside the
 *   body ellipse, where it did nothing.
 * - **A tail.** The bird template reads as a bird largely because the tail wedge gives it a
 *   direction, and a shape with a direction is an animal. Without one, a body on legs is furniture.
 * - **Legs inboard and thicker.** Thin verticals at the far corners are what legs of a table look
 *   like; an animal's are under its mass.
 *
 * Coordinates are normalised — `nx` across the drawing, `ny` up from the ground line as a fraction
 * of the creature's height — so the same template serves a chipmunk and an elk unchanged.
 */
function quadruped(a: DrawArgs): string {
  const left = a.cx - a.w / 2;
  const px = (nx: number): number => left + a.w * nx;
  const py = (ny: number): number => a.baseline - a.h * ny;
  const sx = (n: number): number => a.w * n;
  const sy = (n: number): number => a.h * n;

  const legW = Math.max(1.3, sx(0.1));
  const rect = (nx: number, ny: number, fill: string): string =>
    `<rect x="${round(px(nx))}" y="${round(py(ny))}" width="${round(legW)}" height="${round(sy(ny))}" fill="${fill}"/>`;
  const oval = (nx: number, ny: number, rx: number, ry: number, fill: string): string =>
    `<ellipse cx="${round(px(nx))}" cy="${round(py(ny))}" rx="${round(Math.max(0.6, sx(rx)))}" ry="${round(Math.max(0.6, sy(ry)))}" fill="${fill}"/>`;

  return [
    rect(0.24, 0.44, a.body),
    rect(0.58, 0.44, a.body),
    // Haunch behind the barrel: an animal's mass is over its hind legs, and the double curve is what
    // separates a body from a cylinder.
    oval(0.26, 0.63, 0.17, 0.2, a.body),
    oval(0.44, 0.62, 0.28, 0.19, a.body),
    // Neck, rising clear of the barrel to the head.
    `<path d="M ${round(px(0.6))} ${round(py(0.72))} L ${round(px(0.73))} ${round(py(0.98))} L ${round(px(0.84))} ${round(py(0.95))} L ${round(px(0.7))} ${round(py(0.6))} Z" fill="${a.body}"/>`,
    oval(0.82, 0.9, 0.13, 0.11, a.body),
    // Ear. Small, and the last thing that stops a head reading as a knob.
    oval(0.77, 1.02, 0.035, 0.08, a.body),
    // Tail in the accent colour — the cottontail's scut, the whitetail's flag.
    oval(0.1, 0.72, 0.06, 0.075, a.accent),
    oval(0.87, 0.93, 0.022, 0.03, a.accent),
  ].join('');
}

function bird(a: DrawArgs): string {
  const legTop = a.airborne ? 0 : a.h * 0.28;
  const bodyH = a.h * 0.5;
  const bodyY = a.baseline - legTop - bodyH;
  const headR = Math.max(1.1, a.h * 0.17);
  const x = a.cx - a.w / 2;
  const parts: string[] = [];

  if (!a.airborne) {
    const legW = Math.max(0.7, a.w * 0.05);
    parts.push(
      `<rect x="${round(a.cx - legW)}" y="${round(a.baseline - legTop)}" width="${round(legW)}" height="${round(legTop)}" fill="${a.body}"/>`,
      `<rect x="${round(a.cx + legW * 0.6)}" y="${round(a.baseline - legTop)}" width="${round(legW)}" height="${round(legTop)}" fill="${a.body}"/>`,
    );
  }
  // Tail, body, head — back to front.
  parts.push(
    `<path d="M ${round(x + a.w * 0.16)} ${round(bodyY + bodyH * 0.5)} L ${round(x - a.w * 0.08)} ${round(bodyY + bodyH * 0.9)} L ${round(x + a.w * 0.22)} ${round(bodyY + bodyH * 0.82)} Z" fill="${a.accent}"/>`,
    `<ellipse cx="${round(x + a.w * 0.46)}" cy="${round(bodyY + bodyH / 2)}" rx="${round(a.w * 0.34)}" ry="${round(bodyH / 2)}" fill="${a.body}"/>`,
    `<circle cx="${round(x + a.w * 0.82)}" cy="${round(bodyY + bodyH * 0.18)}" r="${round(headR)}" fill="${a.accent}"/>`,
  );
  if (a.airborne) {
    parts.push(
      `<path d="M ${round(x + a.w * 0.4)} ${round(bodyY + bodyH * 0.3)} L ${round(x + a.w * 0.05)} ${round(bodyY - bodyH * 0.5)} L ${round(x + a.w * 0.62)} ${round(bodyY + bodyH * 0.2)} Z" fill="${a.accent}" fill-opacity="0.8"/>`,
    );
  }
  return parts.join('');
}

function insect(a: DrawArgs): string {
  const bodyH = Math.max(1.6, a.h * 0.7);
  const cy = a.baseline - a.h * 0.5;
  const wingSpan = Math.max(3, a.w * 1.6);
  return [
    `<ellipse cx="${round(a.cx - wingSpan * 0.22)}" cy="${round(cy - bodyH * 0.35)}" rx="${round(wingSpan * 0.28)}" ry="${round(bodyH * 0.75)}" fill="${a.accent}" fill-opacity="0.85"/>`,
    `<ellipse cx="${round(a.cx + wingSpan * 0.22)}" cy="${round(cy - bodyH * 0.35)}" rx="${round(wingSpan * 0.28)}" ry="${round(bodyH * 0.75)}" fill="${a.accent}" fill-opacity="0.85"/>`,
    `<ellipse cx="${round(a.cx)}" cy="${round(cy)}" rx="${round(Math.max(0.9, a.w * 0.28))}" ry="${round(bodyH * 0.6)}" fill="${a.body}"/>`,
  ].join('');
}

function squat(a: DrawArgs): string {
  const cy = a.baseline - a.h * 0.5;
  return [
    `<ellipse cx="${round(a.cx)}" cy="${round(cy)}" rx="${round(a.w * 0.5)}" ry="${round(a.h * 0.5)}" fill="${a.body}"/>`,
    `<circle cx="${round(a.cx + a.w * 0.22)}" cy="${round(a.baseline - a.h * 0.86)}" r="${round(Math.max(0.9, a.h * 0.16))}" fill="${a.accent}"/>`,
  ].join('');
}

function serpentine(a: DrawArgs): string {
  const cy = a.baseline - a.h * 0.5;
  return [
    `<ellipse cx="${round(a.cx - a.w * 0.1)}" cy="${round(cy)}" rx="${round(a.w * 0.46)}" ry="${round(a.h * 0.46)}" fill="${a.body}"/>`,
    `<ellipse cx="${round(a.cx + a.w * 0.4)}" cy="${round(cy)}" rx="${round(a.w * 0.16)}" ry="${round(a.h * 0.36)}" fill="${a.accent}"/>`,
  ].join('');
}

function figure(a: DrawArgs): string {
  const legTop = a.h * 0.47;
  const shoulderY = a.baseline - a.h * 0.82;
  const headR = Math.max(1.1, a.h * 0.07);
  // Width from *height*, not from `a.w`.
  //
  // Every other template is a side elevation, so its width comes from the record's `lengthMeters`.
  // A scale figure is drawn front-on — that is the convention it belongs to — and its length is a
  // person's 0.30 m depth, which produced a lollipop on a stick. Shoulders are 0.28 of height, which
  // is 0.50 / 1.75 exactly: the same record, read on the axis the drawing is actually showing.
  const torsoW = Math.max(1.6, a.h * 0.28);
  const legW = Math.max(0.9, torsoW * 0.3);

  return [
    `<rect x="${round(a.cx - legW * 1.2)}" y="${round(a.baseline - legTop)}" width="${round(legW)}" height="${round(legTop)}" fill="${a.body}"/>`,
    `<rect x="${round(a.cx + legW * 0.2)}" y="${round(a.baseline - legTop)}" width="${round(legW)}" height="${round(legTop)}" fill="${a.body}"/>`,
    // Torso as a taper — shoulders wider than the waist, the whole point of the convention.
    `<path d="M ${round(a.cx - torsoW / 2)} ${round(shoulderY)} L ${round(a.cx + torsoW / 2)} ${round(shoulderY)} L ${round(a.cx + torsoW * 0.3)} ${round(a.baseline - legTop)} L ${round(a.cx - torsoW * 0.3)} ${round(a.baseline - legTop)} Z" fill="${a.body}"/>`,
    `<circle cx="${round(a.cx)}" cy="${round(shoulderY - headR * 1.5)}" r="${round(headR)}" fill="${a.accent}"/>`,
  ].join('');
}
